import { FormStep } from '@sec-loans-ui/loan-offer/steps/FormStep'
import { UnorderedChildrenList } from '@ui-common/components/UnorderedList'
import { useWizardForm } from '@ui-common/hooks'
import { ExternalLink } from '@ui-components/actions/ExternalLink'
import { LooseFormSectionContent } from '@ui-components/containers/LooseSectionContent'
import { MediumSectionContent } from '@ui-components/containers/MediumSectionContent'
import { DataSummary, DataSummaryItem } from '@ui-components/form/DataSummary'
import { FlexColumnContainer, PaddedDiv } from '@ui-components/global/CommonStyling'
import { TwoColumnLayout } from '@ui-components/layout/TwoColumnLayout'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { AdditionalText, LabelText, LeadText } from '@ui-components/typography/Typography'
import { FormattedMessage, useIntl } from 'react-intl'

import { ReservationFooter } from '../../components/ReservationFooter'
import { useExistingLoanOfferContext } from '../LoanOfferContext'
import { getInstallmentData } from './instalmentStepUtils'

export const InstallmentStep = () => {
  const { proceed, loanOffer } = useExistingLoanOfferContext()
  const intl = useIntl()

  const methods = useWizardForm({
    defaultValues: loanOffer,
  })

  const data = getInstallmentData(loanOffer, intl)

  return (
    <FormStep
      allowBack={true}
      subHeader={intl.formatMessage({ id: 'loan-offer-installment-step-heading' })}
      formMethods={methods}
      onSubmit={proceed}
      customFooter={
        <ReservationFooter loanOfferMode={loanOffer.loanOfferMode} reservationLink={loanOffer.reservationLink} />
      }
    >
      <LooseFormSectionContent>
        <TwoColumnLayout
          leftContent={
            <SecondaryHeading
              header={intl.formatMessage({ id: 'loan-offer-installment-step-offer-interest-rate-heading' })}
            />
          }
          rightContent={
            <FlexColumnContainer>
              <MediumSectionContent>
                <DataSummary>
                  <DataSummaryItem
                    id="installment-amount"
                    label={intl.formatMessage({ id: 'loan-offer-installment-step-installments-label' })}
                    value={data.installmentAmount}
                  />
                  <DataSummaryItem
                    id="interest-amount"
                    label={intl.formatMessage({ id: 'loan-offer-installment-step-interests-label' })}
                    value={data.interestAmount}
                  />
                  <DataSummaryItem
                    id="fees-amount"
                    label={intl.formatMessage({ id: 'loan-offer-debitfee-label' })}
                    value={data.feesAmount}
                  />
                  {data.savingsAmount && (
                    <DataSummaryItem
                      id="savings-amount"
                      label={intl.formatMessage({ id: 'loan-offer-installment-step-saving-label' })}
                      value={data.savingsAmount}
                    />
                  )}
                  {data.protectionsAmount && (
                    <DataSummaryItem
                      id="protections-amount"
                      label={intl.formatMessage({ id: 'loan-offer-installment-step-protection-label' })}
                      description={intl.formatMessage({ id: 'loan-offer-installment-step-protection-description' })}
                      value={data.protectionsAmount}
                    />
                  )}
                  <DataSummaryItem
                    id="total-amount"
                    label={intl.formatMessage({ id: 'loan-offer-installment-step-total-label' })}
                    value={<LeadText weight="bold">{data.totalAmount}</LeadText>}
                  />
                </DataSummary>
                {data.links.length > 0 && (
                  <PaddedDiv>
                    <AdditionalText id="loan-offer-protection-documents">
                      {intl.formatMessage({ id: 'loan-offer-protection-documents-list-description' })}
                    </AdditionalText>
                    <UnorderedChildrenList ariaLabelledBy="loan-offer-protection-documents">
                      {data.links.map((link) => (
                        <ExternalLink key={link.text} href={link.url}>
                          {link.text}
                        </ExternalLink>
                      ))}
                    </UnorderedChildrenList>
                  </PaddedDiv>
                )}
              </MediumSectionContent>
            </FlexColumnContainer>
          }
        />
      </LooseFormSectionContent>
      {data.totalAmountWithHigherInterest && (
        <LooseFormSectionContent>
          <TwoColumnLayout
            leftContent={
              <SecondaryHeading
                header={intl.formatMessage({ id: 'loan-offer-installment-step-higher-interest-rate-heading' })}
                leadText={intl.formatMessage({ id: 'loan-offer-installment-step-higher-interest-rate-text' })}
                additionalText={intl.formatMessage({
                  id: 'loan-offer-installment-step-higher-interest-rate-description',
                })}
              />
            }
            rightContent={
              <FlexColumnContainer>
                <LabelText>
                  <FormattedMessage id="sec-loans-payment-plan-info-calculated-monthly-repayment-label" />
                </LabelText>
                <AdditionalText>
                  <FormattedMessage
                    id="sec-loans-payment-plan-info-calculated-monthly-repayment-description"
                    values={{ percentage: '6' }}
                  />
                </AdditionalText>
                <LeadText weight="bold">{data.totalAmountWithHigherInterest}</LeadText>
              </FlexColumnContainer>
            }
          />
        </LooseFormSectionContent>
      )}
    </FormStep>
  )
}
